import { isEmpty } from "lodash";
import { t } from "@/i18n-js/instance";
import { AlignmentField } from "@circle-react-shared/BuilderUI/Editor/fields/AlignmentField";
import { SliderField } from "@circle-react-shared/BuilderUI/Editor/fields/SliderField";
import { useCustomColors } from "@circle-react-shared/BuilderUI/ThemeConfiguration/hooks/useCustomColors";
import { Form } from "@circle-react-uikit/Form";
import { EditorCollapsable } from "../../../../Editor/EditorCollapsable";
import type { ButtonVariant } from "./ButtonStateSelector";
import { generateFormItemName } from "./Styles";
import {
  FONT_STYLE_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  TEXT_ALIGN_OPTIONS,
  TEXT_DECORATION_OPTIONS,
  TEXT_TRANSFORM_OPTIONS,
} from "./constants";

interface TextProps {
  selectedButtonVariant: ButtonVariant;
}

export const Text = ({ selectedButtonVariant }: TextProps) => {
  const {
    flattenedCssVariables,
    themeColors,
    customColors,
    handleAddCustomColor,
  } = useCustomColors();

  return (
    <EditorCollapsable
      title={t("builder_ui.prop_type.styles.text")}
      defaultOpen
    >
      <div className="px-6 py-5">
        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "color")}
          label={t("builder_ui.prop_type.styles.color")}
          hideDescription
          hideBorder
          labelClassName="!font-normal !text-xs"
          className="!flex !w-full !items-center !justify-between !p-0"
        >
          <Form.ColorPickerV2
            name={generateFormItemName(selectedButtonVariant, "color")}
            cssVariables={flattenedCssVariables}
            shouldResolveCssVariable={Boolean(flattenedCssVariables)}
            shouldRenderThemeColors={!isEmpty(themeColors)}
            themeColors={themeColors}
            customColors={customColors}
            onAddCustomColor={
              !isEmpty(themeColors) ? handleAddCustomColor : undefined
            }
          />
        </Form.Item>
        <div className="mt-5">
          <SliderField
            name={generateFormItemName(selectedButtonVariant, "fontSize")}
            label={t("builder_ui.prop_type.styles.font_size")}
            units={["rem", "px"]}
            defaultUnit="rem"
            step={1}
            min={0}
            max={500}
          />
        </div>

        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "fontStyle")}
          label={t("builder_ui.prop_type.styles.font_style")}
          hideDescription
          labelClassName="!font-normal !text-xs"
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(selectedButtonVariant, "fontStyle")}
            options={FONT_STYLE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "fontWeight")}
          label={t("builder_ui.prop_type.styles.font_weight")}
          hideDescription
          labelClassName="!font-normal !text-xs"
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(selectedButtonVariant, "fontWeight")}
            options={FONT_WEIGHT_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "lineHeight")}
          label={t("builder_ui.prop_type.styles.line_height")}
          hideDescription
          hideBorder
          labelClassName="!font-normal !text-xs"
        >
          <Form.Input
            name={generateFormItemName(selectedButtonVariant, "lineHeight")}
            type="number"
            min={0}
            max={5}
            step={0.1}
          />
        </Form.Item>

        <div className="mt-5">
          <AlignmentField
            label={t("builder_ui.prop_type.styles.text_align")}
            name={generateFormItemName(selectedButtonVariant, "textAlign")}
            options={TEXT_ALIGN_OPTIONS}
          />
        </div>

        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "textDecoration")}
          label={t("builder_ui.prop_type.styles.text_decoration")}
          hideDescription
          hideBorder
          labelClassName="!font-normal !text-xs"
        >
          <Form.SelectNative
            name={generateFormItemName(selectedButtonVariant, "textDecoration")}
            options={TEXT_DECORATION_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(selectedButtonVariant, "textTransform")}
          label={t("builder_ui.prop_type.styles.text_transform")}
          hideDescription
          hideBorder
          labelClassName="!font-normal !text-xs"
        >
          <Form.SelectNative
            name={generateFormItemName(selectedButtonVariant, "textTransform")}
            options={TEXT_TRANSFORM_OPTIONS}
          />
        </Form.Item>
      </div>
    </EditorCollapsable>
  );
};
